import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEntriesStore } from '../stores';
import { EntryPresentation, EntryLocationMap } from '../components/entries';
import { Button, Alert } from '../components/ui';
import { api } from '../lib/api';

export function EntryViewPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    currentEntry,
    isLoading,
    error,
    fetchEntry,
    clearCurrentEntry,
    clearError,
  } = useEntriesStore();

  const [previousId, setPreviousId] = useState<string | null>(null);
  const [nextId, setNextId] = useState<string | null>(null);

  const fetchAdjacentIds = useCallback(async (entryId: string) => {
    const [prev, next] = await Promise.all([
      api.getPreviousEntryId(entryId),
      api.getNextEntryId(entryId),
    ]);
    setPreviousId(prev.id);
    setNextId(next.id);
  }, []);

  useEffect(() => {
    clearCurrentEntry();
    setPreviousId(null);
    setNextId(null);
    if (id) {
      fetchEntry(id);
      fetchAdjacentIds(id);
    }

    return () => {
      clearCurrentEntry();
      clearError();
    };
  }, [id, fetchEntry, clearCurrentEntry, clearError, fetchAdjacentIds]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!currentEntry) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/entries')}>
          {t('common.back')}
        </Button>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => previousId && navigate(`/entries/${previousId}/view`)}
            disabled={!previousId}
          >
            ← {t('common.previous')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => nextId && navigate(`/entries/${nextId}/view`)}
            disabled={!nextId}
          >
            {t('common.next')} →
          </Button>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <EntryPresentation entry={currentEntry} />

      {currentEntry.locationLatitude != null && currentEntry.locationLongitude != null && (
        <EntryLocationMap
          latitude={currentEntry.locationLatitude}
          longitude={currentEntry.locationLongitude}
          locationName={currentEntry.locationName}
        />
      )}
    </div>
  );
}
